import { apiClient } from './client';
import type { CaptureSession } from './sessions';

export const INSTANCE_STATUSES = [
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
  'aborted',
] as const;
export type InstanceStatus = (typeof INSTANCE_STATUSES)[number];

export const INSTANCE_STATUS_LABELS: Record<InstanceStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  aborted: 'Aborted',
};

export const TEAM_ROLES = [
  'lead_surgeon',
  'fellow',
  'resident',
  'circulator',
  'scrub_nurse',
  'observer',
  'other',
] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  lead_surgeon: 'Lead surgeon',
  fellow: 'Fellow',
  resident: 'Resident',
  circulator: 'Circulator',
  scrub_nurse: 'Scrub nurse',
  observer: 'Observer',
  other: 'Other',
};

// Event kind reuses the session vocabulary.
export const INSTANCE_KINDS = [
  'live_surgery',
  'simulation',
  'training',
  'assessment',
] as const;
export const INSTANCE_KIND_LABELS: Record<string, string> = {
  live_surgery: 'Live surgery',
  simulation: 'Simulation',
  training: 'Training',
  assessment: 'Assessment',
};

export interface ProcedureInstance {
  id: string;
  procedure_id: string | null;
  procedure_name: string;
  department_id: string | null;
  location_id: string | null;
  kind: string | null;
  status: string;
  scheduled_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  external_case_ref: string | null;
  created_at: string;
  updated_at: string;
  /** Distinct recording participants (first-started order) + how many
   *  recordings are grouped — so the case list reads "Frank, Calvin ·
   *  2 recordings". Default [] / 0 on older payloads. */
  participants: string[];
  session_count: number;
}

export interface TeamAssignment {
  id: string;
  procedure_instance_id: string;
  participant_user_id: string | null;
  participant_label: string | null;
  role: string;
  session_id: string | null;
  device_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

export interface ProcedureInstanceDetail extends ProcedureInstance {
  team: TeamAssignment[];
  sessions: CaptureSession[];
}

export interface ProcedureInstanceCreate {
  procedure_name: string;
  procedure_id?: string | null;
  department_id?: string | null;
  location_id?: string | null;
  kind?: string | null;
  status?: string;
  external_case_ref?: string | null;
}

export type ProcedureInstanceUpdate = Partial<
  Omit<ProcedureInstanceCreate, 'procedure_name'>
> & { procedure_name?: string };

export interface TeamAssignmentCreate {
  role: string;
  participant_user_id?: string | null;
  participant_label?: string | null;
  session_id?: string | null;
}

const base = (tenantId: string) =>
  `/api/v1/tenants/${tenantId}/procedure-instances`;

export async function listInstances(
  tenantId: string,
  params: { status?: string } = {},
): Promise<ProcedureInstance[]> {
  const { data } = await apiClient.get<ProcedureInstance[]>(base(tenantId), {
    params,
  });
  return data;
}

/** Participant read-only: the cases the current participant is part of (a
 *  recording of theirs is attached), across all their tenants. */
export async function listMyCases(): Promise<ProcedureInstance[]> {
  const { data } = await apiClient.get<ProcedureInstance[]>(
    '/api/v1/tenants/me/cases',
  );
  return data;
}

export async function getInstance(
  tenantId: string,
  id: string,
): Promise<ProcedureInstanceDetail> {
  const { data } = await apiClient.get<ProcedureInstanceDetail>(
    `${base(tenantId)}/${id}`,
  );
  return data;
}

export async function createInstance(
  tenantId: string,
  body: ProcedureInstanceCreate,
): Promise<ProcedureInstance> {
  const { data } = await apiClient.post<ProcedureInstance>(base(tenantId), body);
  return data;
}

export async function updateInstance(
  tenantId: string,
  id: string,
  body: ProcedureInstanceUpdate,
): Promise<ProcedureInstance> {
  const { data } = await apiClient.patch<ProcedureInstance>(
    `${base(tenantId)}/${id}`,
    body,
  );
  return data;
}

export async function deleteInstance(
  tenantId: string,
  id: string,
): Promise<void> {
  await apiClient.delete(`${base(tenantId)}/${id}`);
}

export async function attachSession(
  tenantId: string,
  id: string,
  sessionRowId: string,
): Promise<void> {
  await apiClient.post(`${base(tenantId)}/${id}/sessions/${sessionRowId}`);
}

export async function detachSession(
  tenantId: string,
  id: string,
  sessionRowId: string,
): Promise<void> {
  await apiClient.delete(`${base(tenantId)}/${id}/sessions/${sessionRowId}`);
}

export interface ReprocessResult {
  recordings_queued: number;
  recordings_skipped: number;
}

/** Re-run scoring for a case's recordings so the Focus overlay's per-recording
 *  traces are (re)generated. Recordings without an uploaded NDJSON are skipped. */
export async function reprocessInstance(
  tenantId: string,
  id: string,
): Promise<ReprocessResult> {
  const { data } = await apiClient.post<ReprocessResult>(
    `${base(tenantId)}/${id}/reprocess`,
  );
  return data;
}

export async function addTeamMember(
  tenantId: string,
  id: string,
  body: TeamAssignmentCreate,
): Promise<TeamAssignment> {
  const { data } = await apiClient.post<TeamAssignment>(
    `${base(tenantId)}/${id}/team`,
    body,
  );
  return data;
}

export async function removeTeamMember(
  tenantId: string,
  id: string,
  assignmentId: string,
): Promise<void> {
  await apiClient.delete(`${base(tenantId)}/${id}/team/${assignmentId}`);
}

/**
 * Recordings a participant may build a case from: their own, plus anyone
 * else's that overlaps one of theirs in time.
 *
 * A separate endpoint from the tenant roster because a participant has no
 * standing right to the roster. The server re-checks every id on create —
 * this list only decides what gets offered.
 */
export async function listMyCaseCandidates(): Promise<CaptureSession[]> {
  const { data } = await apiClient.get<CaptureSession[]>(
    '/api/v1/tenants/me/cases/candidates',
  );
  return data;
}

/**
 * Create a case as a participant.
 *
 * No tenant id: the server derives it from the chosen recordings, so a
 * selection spanning two organisations is a validation error rather than a
 * silent half-write. Creation and attachment are one call for the same
 * reason — a participant has no way to repair a case that was created but
 * left empty.
 */
export async function createMyCase(body: {
  procedure_name: string;
  external_case_ref?: string | null;
  session_ids: string[];
}): Promise<ProcedureInstance> {
  const { data } = await apiClient.post<ProcedureInstance>(
    '/api/v1/tenants/me/cases',
    body,
  );
  return data;
}
